import { useFormContext } from 'react-hook-form'
import { Select } from '../ui/Select'
import { OptionCard } from '../ui/OptionCard'
import type { FormData } from './FormWizard'

const sizeOptions = [
  { value: 'small', label: 'Small (under 50 gallons)' },
  { value: 'medium', label: 'Medium (50-150 gallons)' },
  { value: 'large', label: 'Large (150-500 gallons)' },
  { value: 'xlarge', label: 'Extra Large (500+ gallons)' },
  { value: 'custom', label: 'Custom / Not sure' },
]

const typeOptions = [
  { value: 'reef', label: 'Reef Tank' },
  { value: 'fish-only', label: 'Fish Only' },
  { value: 'planted', label: 'Planted Tank' },
  { value: 'pond', label: 'Pond / Water Feature' },
  { value: 'other', label: 'Other / Not sure' },
]

const waterTypeOptions = [
  {
    value: 'saltwater',
    label: 'Saltwater',
    description: 'Marine fish, coral, reef systems',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    ),
  },
  {
    value: 'freshwater',
    label: 'Freshwater',
    description: 'Tropical fish, goldfish, planted tanks',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    value: 'unsure',
    label: 'Not Sure',
    description: "I'd like expert guidance",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
]

export function StepAquariumDetails() {
  const { register, watch, formState: { errors } } = useFormContext<FormData>()
  const selectedWaterType = watch('water_type')

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Tell us about your aquarium
        </h2>
        <p className="text-gray-600">
          This helps us understand your project better
        </p>
      </div>

      <div className="space-y-6">
        {/* Water Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Water Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {waterTypeOptions.map((option) => (
              <OptionCard
                key={option.value}
                {...register('water_type')}
                value={option.value}
                label={option.label}
                description={option.description}
                icon={option.icon}
                selected={selectedWaterType === option.value}
              />
            ))}
          </div>
        </div>

        {/* Size */}
        <Select
          {...register('aquarium_size')}
          label="Aquarium Size"
          options={sizeOptions}
          placeholder="Select size..."
          error={errors.aquarium_size?.message}
        />

        {/* Type */}
        <Select
          {...register('aquarium_type')}
          label="Aquarium Type"
          options={typeOptions}
          placeholder="Select type..."
          error={errors.aquarium_type?.message}
        />
      </div>
    </div>
  )
}
